import type {BaseTextFieldProps as MuiBaseTextFieldProps} from '@mui/material/TextField';
import React from 'react';
import type {FormControlProps as DefaultFormControlProps} from '../Form/FormControl';
import {FormControl} from '../Form/FormControl';
import {FormHelperText} from '../Form/FormHelperText';
import {useId} from '../hooks';
import {InputLabel} from '../Input/InputLabel';
import {Select, SelectProps as BaseSelectProps} from '../Select/Select';

type ModifiedTextFieldProps = Omit<MuiBaseTextFieldProps, 'color' | 'size' | 'select' | 'SelectProps' | 'margin' | 'onKeyDown' | 'onKeyUp' | 'variant' | 'type' | 'ref' | 'classes' | 'inputProps'>;
export interface BaseSelectFieldProps extends ModifiedTextFieldProps, BaseSelectProps {}

export interface SelectFieldProps extends BaseSelectFieldProps {
    /**
     * IDs are automatically generated by component for accessibility purposes.
     * Use this prop to pass a specific ID value that will be appended.
     * @default "";
     */
    id?: string;
    /**
     * Exposing FormControl API;
     * @default {}
     */
    FormControlProps?: DefaultFormControlProps;
}

export const SelectField = React.forwardRef<HTMLDivElement, SelectFieldProps>(({
    label, FormControlProps, InputLabelProps, sx, id = '',
    children, onClick, className, fullWidth = true, disabled, required,
    hiddenLabel, inputRef, inputProps, helperText, name,
    error, variant, ...props
}, ref) => {
    const generatedId = useId(id, 'selectField');

    return (
        <FormControl
            disabled={disabled}
            fullWidth={fullWidth}
            required={required}
            sx={sx}
            {...FormControlProps}
        >
            {label &&
                <InputLabel
                    {...InputLabelProps}
                    disabled={disabled}
                    hiddenLabel={hiddenLabel}
                    htmlFor={generatedId}
                >{label}</InputLabel>
            }
            <Select
                {...props}
                disabled={disabled}
                error={error}
                fullWidth={fullWidth}
                id={generatedId}
                inputProps={inputProps}
                inputRef={inputRef}
                name={name || generatedId}
                ref={ref}
            >{children}</Select>
            {helperText && (
                <FormHelperText
                    disabled={disabled}
                    error={error}
                >{helperText}</FormHelperText>
            )}
        </FormControl>
    );
});

SelectField.displayName = 'SelectField';
